import { G } from '@mobily/ts-belt';
import {
  // eslint-disable-next-line @typescript-eslint/no-restricted-imports -- this file is the wrapper; direct import is intentional
  DateInput as DateInputBase,
  DateInputProps,
  Validator,
} from 'react-admin';

import { type DateString, tryToDateString } from '@sh4res/bo-common/utils/date';
import {
  MAX_SUPPORTED_DATE,
  MIN_SUPPORTED_DATE,
  supportedDateRangeValidators,
} from '@sh4res/bo-common/validators/date';

export const isSupportedDateString = (value: string): value is DateString => {
  const day = tryToDateString(value);
  return day === value && day >= MIN_SUPPORTED_DATE && day <= MAX_SUPPORTED_DATE;
};

const toValidatorArray = (validate: DateInputProps['validate']): Validator[] => {
  if (Array.isArray(validate)) {
    return validate;
  }
  return G.isNotNullable(validate) ? [validate] : [];
};

/**
 * Displays any date-like value (`YYYY-MM-DD`, ISO string, `Date`) as a calendar day.
 * An empty string keeps the input controlled.
 */
const formatDateInputValue = (value: unknown): string => tryToDateString(value) ?? '';

const parseDateInputValue = (value: string): DateString | null => tryToDateString(value);

/**
 * RA DateInput wrapper storing a calendar day (`DateString`, `YYYY-MM-DD`) or `null` in the form.
 * Values outside the range 1900–9999 are rejected.
 */
export const DateInput = ({ inputProps, validate, ...props }: DateInputProps) => (
  <DateInputBase
    format={formatDateInputValue}
    parse={parseDateInputValue}
    inputProps={{ min: MIN_SUPPORTED_DATE, max: MAX_SUPPORTED_DATE, ...inputProps }}
    validate={[...supportedDateRangeValidators, ...toValidatorArray(validate)]}
    {...props}
  />
);
